/**
 * Age of Steam - Game State to UI Props Adapter
 *
 * このファイルは、ゲーム状態（BarbadosGameState）からUI Props形式への変換を行います。
 * Lovable側で作成したPresentational Componentと、既存のロジックを接続するための橋渡し役です。
 *
 * @see src/types/ui-interfaces.ts
 * @see FRONTEND.md
 */

import type { Ctx } from 'boardgame.io';
import type { BarbadosGameState, BarbadosPlayer, BarbadosActionType, TileType } from '../types';
import { getIncomeReduction as calcIncomeReduction, getIncomeReductionDetroit as calcIncomeReductionDetroit, computeTotalExpenses } from '../types';
import type {
  GameContext,
  PhaseName,
  IssueSharesPhaseProps,
  SelectActionsPhaseProps,
  BuildTrackPhaseProps,
  MoveGoodsPhaseProps,
  RefillGoodsPhaseProps,
  AutoPhaseProps,
  PlayerStatsProps,
  GameEndScreenProps,
  TransportableGoods,
  BestRoute,
  GameMoves,
  SeattleMapOverlayData,
} from '../types/ui-interfaces';
import { TILE_DEFINITIONS, ALL_TILE_IDS } from '../game/constants/tiles';
import { findAllPaths, calculateExpectedIncome } from '../game/utils/pathfinder';
import { validateBuildTrack } from '../game/validators/buildTrackValidator';
import { SEATTLE_DICE_GROUPS, SEATTLE_RULES } from '../../data/map/seattle_metro/config';
import { calculateBuildCost } from '../game/utils/buildCostCalculator';
import { debug } from '../utils/debug';

// ============================================================================
// 1. コンテキスト抽出
// ============================================================================

/**
 * ゲーム状態からコンテキストを抽出
 */
export function extractContext(
  G: BarbadosGameState,
  ctx: { phase: string | null; currentPlayer: string; gameover?: unknown }
): GameContext {
  return {
    phase: (ctx.phase as PhaseName) || 'Completed',
    currentTurn: G.actualTurn || 1,
    currentPlayerId: ctx.currentPlayer,
    isGameOver: G.isGameOver || ctx.gameover !== undefined,
    endReason: G.endReason,
  };
}

// ============================================================================
// 2. Phase 1: 株式発行 Props 生成
// ============================================================================

/**
 * Phase 1: 株式発行フェーズのUIプロパティを生成（全マップ共通）
 *
 * mapConfig.maxSharesPerTurn でターン制限を制御:
 * - Barbados: 1（1ターン1株まで）
 * - Puerto Rico/Detroit: undefined（無制限）
 */
export function createIssueSharesProps(
  G: BarbadosGameState,
  isMyTurn: boolean,
  moves: GameMoves
): IssueSharesPhaseProps {
  const player = G.players['0'] as BarbadosPlayer;
  const maxSharesPerTurn = G.mapConfig?.maxSharesPerTurn;
  const maxSharesLimit = G.mapConfig?.maxSharesLimit ?? 15;

  // ターン制限がある場合は適用
  let maxIssueCount = maxSharesLimit - player.sharesIssued;
  if (maxSharesPerTurn !== undefined) {
    const remainingThisTurn = maxSharesPerTurn - player.currentTurnSharesIssued;
    maxIssueCount = Math.min(maxIssueCount, remainingThisTurn);
  }

  return {
    player: {
      cash: player.cash,
      sharesIssued: player.sharesIssued,
    },
    maxIssueCount,
    maxSharesLimit,
    isMyTurn,
    onIssueShares: (count: number) => {
      moves.issueShares({ issueCount: count });
    },
    helpTextKey: G.mapConfig?.issueSharesHelpKey,
  };
}

// ============================================================================
// 3. Phase 3: アクション選択 Props 生成
// ============================================================================

/**
 * Phase 3: アクション選択フェーズのUIプロパティを生成
 */
export function createSelectActionsProps(
  G: BarbadosGameState,
  isMyTurn: boolean,
  moves: GameMoves
): SelectActionsPhaseProps {
  const actionMarkers = G.actionMarkers || {
    ENGINEER: false,
    LOCOMOTIVE: false,
    URBANIZATION: false,
    PRODUCTION: false,
  };

  const allMarkersUsed = Object.values(actionMarkers).every((used) => used);

  return {
    actionMarkers,
    allMarkersUsed,
    isMyTurn,
    onSelectAction: (action: BarbadosActionType) => {
      moves.selectAction({ action });
    },
  };
}

// ============================================================================
// 4. Phase 4: 線路建設 Props 生成
// ============================================================================

/**
 * タイル残り枚数を計算
 */
function calculateRemainingTiles(G: BarbadosGameState, tileType: TileType): number {
  const tileDef = TILE_DEFINITIONS[tileType];
  if (!tileDef) return 0;

  const usedCount = G.map.filter((hex) => hex.tile?.type === tileType).length;
  return tileDef.stock - usedCount;
}

/**
 * Phase 4: 線路建設フェーズのUIプロパティを生成
 */
export function createBuildTrackProps(
  G: BarbadosGameState,
  ctx: Ctx,
  isMyTurn: boolean,
  moves: GameMoves,
  temporaryPlacement: {
    hexCoordinate: { q: number; r: number };
    tileType: TileType;
    rotation: 0 | 1 | 2 | 3 | 4 | 5;
  } | null,
  onSetTemporaryPlacement: (placement: {
    hexCoordinate: { q: number; r: number };
    rotation: 0 | 1 | 2 | 3 | 4 | 5;
  } | null) => void
): BuildTrackPhaseProps {
  const player = G.players['0'] as BarbadosPlayer;

  // 配置可能枚数を計算
  const maxTilesThisTurn = player.selectedAction === 'ENGINEER' ? 4 : 3;
  const tilesRemaining = maxTilesThisTurn - player.tilesPlacedThisTurn;
  const newCityTilesPlaced = player.newCityTilesPlacedThisTurn ?? 0;
  const newCityTilesRemaining = 1 - newCityTilesPlaced;

  // 利用可能なタイル一覧を生成
  const allowedNewCityTiles = G.mapConfig?.availableNewCityTiles ?? [];
  const availableTiles = ALL_TILE_IDS
    .filter((id) => {
      // 新都市タイルはmapConfigで許可されたもののみ
      if (id.startsWith('NEW_CITY_')) {
        return allowedNewCityTiles.includes(id as TileType);
      }
      return true;
    })
    .map((id) => {
      const def = TILE_DEFINITIONS[id];
      const remaining = calculateRemainingTiles(G, id as TileType);
      const isNewCityTileDisabled = def.isNewCity && player.selectedAction !== 'URBANIZATION';

      return {
        type: id as TileType,
        name: def.name,
        remaining,
        isTown: def.isTown,
        isNewCity: def.isNewCity,
        isDisabled: remaining === 0 || isNewCityTileDisabled,
      };
    });

  // 仮配置のバリデーションと追加情報を計算
  let temporaryPlacementWithValidation: BuildTrackPhaseProps['temporaryPlacement'] = null;

  if (temporaryPlacement) {
    const targetHex = G.map.find(
      (h) =>
        h.coordinate.q === temporaryPlacement.hexCoordinate.q &&
        h.coordinate.r === temporaryPlacement.hexCoordinate.r
    );

    const isNewCityTile = TILE_DEFINITIONS[temporaryPlacement.tileType]?.isNewCity === true;
    let placementType: 'place' | 'replace' = 'place';
    let validationError: string | null = null;
    let validationErrorCode: string | undefined = undefined;

    if (isNewCityTile) {
      const validation = validateBuildTrack(G, ctx, {
        hexCoordinate: temporaryPlacement.hexCoordinate,
        tileType: temporaryPlacement.tileType,
        rotation: temporaryPlacement.rotation,
        placementType: 'place',
      });
      if (!validation.success) {
        validationError = validation.message || validation.error || 'Invalid placement';
        validationErrorCode = validation.error;
      }
    } else if (targetHex?.tile !== null) {
      const replaceValidation = validateBuildTrack(G, ctx, {
        hexCoordinate: temporaryPlacement.hexCoordinate,
        tileType: temporaryPlacement.tileType,
        rotation: temporaryPlacement.rotation,
        placementType: 'replace',
      });
      if (replaceValidation.success) {
        placementType = 'replace';
      } else {
        validationError = replaceValidation.message || replaceValidation.error || 'Invalid replacement';
        validationErrorCode = replaceValidation.error;
      }
    } else {
      const validation = validateBuildTrack(G, ctx, {
        hexCoordinate: temporaryPlacement.hexCoordinate,
        tileType: temporaryPlacement.tileType,
        rotation: temporaryPlacement.rotation,
        placementType: 'place',
      });
      if (!validation.success) {
        validationError = validation.message || validation.error || 'Invalid placement';
        validationErrorCode = validation.error;
      }
    }

    const cost = targetHex
      ? (isNewCityTile ? 0 : calculateBuildCost(targetHex, placementType, temporaryPlacement.tileType))
      : 0;

    temporaryPlacementWithValidation = {
      hexCoordinate: temporaryPlacement.hexCoordinate,
      tileType: temporaryPlacement.tileType,
      rotation: temporaryPlacement.rotation,
      placementType,
      cost,
      validationError,
      validationErrorCode,
    };
  }

  return {
    player: {
      cash: player.cash,
      tilesPlacedThisTurn: player.tilesPlacedThisTurn,
      newCityTilesPlacedThisTurn: player.newCityTilesPlacedThisTurn ?? 0,
      selectedAction: player.selectedAction as BarbadosActionType | null,
    },
    maxTilesThisTurn,
    tilesRemaining,
    newCityTilesRemaining,
    availableTiles: availableTiles as BuildTrackPhaseProps['availableTiles'],
    mapName: G.mapName,
    isMyTurn,
    townDisksRemaining: G.townDisksRemaining ?? 8,
    // 建設コスト情報（バックエンドで計算済みをそのまま使用）
    buildCostInfo: G.buildCostInfo ?? {
      totalCost: 0,
      discount: 0,
      finalCost: 0,
      isOverBudget: false,
    },
    temporaryPlacement: temporaryPlacementWithValidation,
    selectedTileType: temporaryPlacement?.tileType ?? null,
    onSelectTile: () => {
      // タイル選択は親コンポーネントで管理
    },
    onSetTemporaryPlacement,
    onRotate: () => {
      // 回転は親コンポーネントで管理
    },
    onConfirmPlacement: () => {
      if (temporaryPlacementWithValidation && !temporaryPlacementWithValidation.validationError) {
        moves.buildTrack({
          hexCoordinate: temporaryPlacementWithValidation.hexCoordinate,
          tileType: temporaryPlacementWithValidation.tileType,
          rotation: temporaryPlacementWithValidation.rotation,
          placementType: temporaryPlacementWithValidation.placementType,
        });
      }
    },
    onFinishBuild: () => {
      moves.passBuildTrack();
    },
  };
}

// ============================================================================
// 5. Phase 5: 商品輸送 Props 生成
// ============================================================================

/**
 * 輸送可能な商品を検出
 *
 * マップ上のどこにある商品キューブでも、同色の City（または NewCity）に運べる。
 * 出発地は City, NewCity, Town のいずれでも可。
 */
export function findTransportableGoods(G: BarbadosGameState, locomotiveLevel: number): TransportableGoods[] {
  const goods: TransportableGoods[] = [];

  for (const hex of G.map) {
    // 商品がなければスキップ
    if (hex.goods.length === 0) continue;

    // 出発地の情報を取得（City, NewCity, Town のいずれか）
    let sourceId: string;
    let displayLabel: string;

    if (hex.city) {
      // City からの出発
      sourceId = hex.city.id;
      displayLabel = hex.city.newCityId
        ? hex.city.newCityId
        : `Light ${hex.city.diceNumber}`;
    } else if (hex.town?.newCity) {
      // NewCity（都市化された町）からの出発
      sourceId = hex.town.newCity.id;
      displayLabel = hex.town.newCity.newCityId || hex.town.newCity.id;
    } else if (hex.town) {
      // 通常の Town からの出発
      sourceId = hex.town.id;
      displayLabel = hex.town.id;
    } else {
      // 商品があるが出発地情報がない場合（通常は発生しない）
      continue;
    }

    for (const goodsColor of hex.goods) {
      // 目的地は同色の City または NewCity（Town は目的地にならない）
      const destinations = G.map.filter((destHex) => {
        const destCity = destHex.city || destHex.town?.newCity;
        if (!destCity) return false;
        // 同色の都市で、出発地と異なる
        // color が配列の場合は複数色を受け入れる（例: SAN JUAN は red と black を受け入れる）
        // Gray City (color: ['']) は何色でも受け入れる（Seattle Metro）
        const colorArray = Array.isArray(destCity.color) ? destCity.color : [destCity.color];
        const isGrayCity = colorArray.length === 1 && (colorArray[0] as string) === '';
        const acceptsColor = isGrayCity || colorArray.includes(goodsColor);
        return acceptsColor && destCity.id !== sourceId;
      });

      // 全ての有効な経路を収集してリンク数を取得
      const allLinkCounts = new Set<number>();
      for (const destHex of destinations) {
        const destCity = destHex.city || destHex.town?.newCity;
        const paths = findAllPaths(G, sourceId, destCity!.id, goodsColor, locomotiveLevel);
        for (const path of paths) {
          allLinkCounts.add(path.length);
        }
      }

      if (allLinkCounts.size > 0) {
        // リンク数を昇順でソート
        const availableLinkCounts = Array.from(allLinkCounts).sort((a, b) => a - b);
        goods.push({ cityId: sourceId, color: goodsColor, displayLabel, availableLinkCounts });
      }
    }
  }

  return goods;
}

/**
 * 全ての有効な経路を計算（リンク数でグループ化）
 *
 * Request 9: 手動経路選択対応
 * - 全経路を計算
 * - リンク数でグループ化し、各グループから最も収入の高い経路を1つ選択
 * - 最も収入の高い経路をbestRouteとして返し、それ以外をalternativeRoutesとして返す
 */
export function findAllRoutes(
  G: BarbadosGameState,
  selectedGoods: TransportableGoods,
  locomotiveLevel: number
): { bestRoute: BestRoute | null; alternativeRoutes: BestRoute[] } {
  const destinations = G.map.filter((destHex) => {
    const destCity = destHex.city || destHex.town?.newCity;
    if (!destCity) return false;
    // color が配列の場合は複数色を受け入れる（例: SAN JUAN は red と black を受け入れる）
    // Gray City (color: ['']) は何色でも受け入れる（Seattle Metro）
    const colorArray = Array.isArray(destCity.color) ? destCity.color : [destCity.color];
    const isGrayCity = colorArray.length === 1 && (colorArray[0] as string) === '';
    const acceptsColor = isGrayCity || colorArray.includes(selectedGoods.color);
    return acceptsColor && destCity.id !== selectedGoods.cityId;
  });

  // リンク数ごとにグループ化
  const routesByLinkCount: Map<number, BestRoute[]> = new Map();

  debug.log(`[findAllRoutes] from ${selectedGoods.cityId}, destinations:`, destinations.map(d => (d.city || d.town?.newCity)?.id));

  for (const destHex of destinations) {
    const destCity = destHex.city || destHex.town?.newCity;
    // 全ての有効な経路を取得（異なるリンク数の経路を全て含む）
    const allPaths = findAllPaths(G, selectedGoods.cityId, destCity!.id, selectedGoods.color, locomotiveLevel);

    for (const path of allPaths) {
      // 共通関数で収入計算（Gray City は 0）
      const totalIncome = calculateExpectedIncome(G, destCity!.id, path);
      const linkCount = path.length;

      const linkDetails = path.map((linkId) => {
        const link = G.completedLinks.find((l) => l.id === linkId);
        return link
          ? { id: link.id, startId: link.startId, endId: link.endId, owner: link.owner }
          : { id: linkId, startId: '?', endId: '?', owner: '?' };
      });

      // 目的地名を取得
      const destinationName = destCity!.name || destCity!.id;

      const route: BestRoute = {
        path,
        destinationId: destCity!.id,
        destinationName,
        income: totalIncome,
        linkCount,
        linkDetails,
      };

      if (!routesByLinkCount.has(linkCount)) {
        routesByLinkCount.set(linkCount, []);
      }
      routesByLinkCount.get(linkCount)!.push(route);
    }
  }

  // 各リンク数グループから最も収入の高い経路を1つ選択
  const bestRoutesPerLinkCount: BestRoute[] = [];
  for (const [_linkCount, routes] of routesByLinkCount) {
    // 収入が最大の経路を選択（同じ収入なら最初のものを使用）
    const best = routes.reduce((a, b) => (b.income > a.income ? b : a), routes[0]);
    bestRoutesPerLinkCount.push(best);
  }

  // 収入順でソート（最高収入が先頭）
  bestRoutesPerLinkCount.sort((a, b) => b.income - a.income);

  if (bestRoutesPerLinkCount.length === 0) {
    return { bestRoute: null, alternativeRoutes: [] };
  }

  // 最高収入の経路をbestRoute、それ以外をalternativeRoutesとする
  const bestRoute = bestRoutesPerLinkCount[0];
  const alternativeRoutes = bestRoutesPerLinkCount.slice(1);

  debug.log(`[findAllRoutes] BEST: dest=${bestRoute.destinationId}, income=${bestRoute.income}, links=${bestRoute.linkCount}`);
  debug.log(`[findAllRoutes] alternatives:`, alternativeRoutes.map(r => ({ dest: r.destinationId, income: r.income, links: r.linkCount })));

  return { bestRoute, alternativeRoutes };
}

/**
 * 最適経路を計算（後方互換用）
 */
export function findBestRoute(
  G: BarbadosGameState,
  selectedGoods: TransportableGoods,
  locomotiveLevel: number
): BestRoute | null {
  const { bestRoute } = findAllRoutes(G, selectedGoods, locomotiveLevel);
  return bestRoute;
}

/**
 * Phase 5: 商品輸送フェーズのUIプロパティを生成
 */
export function createMoveGoodsProps(
  G: BarbadosGameState,
  isMyTurn: boolean,
  moves: GameMoves,
  selectedGoods: TransportableGoods | null,
  selectedRoute?: BestRoute | null
): MoveGoodsPhaseProps {
  const player = G.players['0'] as BarbadosPlayer;
  const transportableGoods = findTransportableGoods(G, player.locomotiveLevel);

  // 全経路を計算
  const { bestRoute, alternativeRoutes } = selectedGoods
    ? findAllRoutes(G, selectedGoods, player.locomotiveLevel)
    : { bestRoute: null, alternativeRoutes: [] };

  // 選択中の経路（ユーザーが代替経路を選択した場合はそれを使用）
  const currentRoute = selectedRoute || bestRoute;

  return {
    player: {
      locomotiveLevel: player.locomotiveLevel,
      incomeLevel: player.incomeLevel,
      cash: player.cash,
      hasUpgradedLocoThisTurn: player.hasUpgradedLocoThisTurn,
      turnsCompletedThisPhase: player.turnsCompletedThisPhase,
    },
    transportableGoods,
    selectedGoods,
    bestRoute: currentRoute,
    alternativeRoutes,
    isMyTurn,
    showUpgradeConfirm: false, // 親コンポーネントで管理
    errorMessage: '', // 親コンポーネントで管理
    onSelectGoods: () => {
      // 商品選択・経路トグルは親コンポーネントで管理
    },
    onSelectRoute: () => {
      // 経路選択は親コンポーネントで管理
    },
    onConfirmTransport: () => {
      if (selectedGoods && currentRoute) {
        moves.moveGoods({
          fromCityId: selectedGoods.cityId,
          toCityId: currentRoute.destinationId,
          goodsColor: selectedGoods.color,
        });
      }
    },
    onUpgradeLoco: () => {
      moves.upgradeLoco();
    },
    onShowUpgradeConfirm: () => {
      // 親コンポーネントで管理
    },
    onHideUpgradeConfirm: () => {
      // 親コンポーネントで管理
    },
    onPass: () => {
      moves.passMoveGoods();
    },
  };
}

// ============================================================================
// 6. Phase 9: 商品補充 Props 生成
// ============================================================================

/**
 * Phase 9: 商品補充フェーズのUIプロパティを生成
 */
export function createRefillGoodsProps(
  G: BarbadosGameState,
  moves: GameMoves,
  onShowGoodsDisplay: () => void,
  onHideGoodsDisplay: () => void,
  showGoodsDisplay: boolean
): RefillGoodsPhaseProps {
  const player = G.players['0'] as BarbadosPlayer;
  const isProductionSelected = player.selectedAction === 'PRODUCTION';

  return {
    isProductionSelected,
    drawnGoods: G.drawnGoods || null,
    productionPlacementCount: G.productionPlacementCount || 0,
    goodsDisplay: G.goodsDisplay,
    showGoodsDisplay,
    onPlaceGoods: isProductionSelected
      ? (side: 'light' | 'dark', columnNumber: number, columnType: 'city' | 'newCity') => {
          const columns = side === 'light' ? G.goodsDisplay.lightSide : G.goodsDisplay.darkSide;
          let column;
          if (columnType === 'newCity') {
            column = columns.find((col) => col.newCityId !== null && col.columnNumber === columnNumber);
          } else {
            column = columns.find((col) => col.cityId !== '' && col.newCityId === null && col.columnNumber === columnNumber);
          }

          if (!column || !G.drawnGoods) return;

          const row = column.cubes.findIndex((c) => c === null);
          if (row === -1) return;

          const placedCount = G.productionPlacementCount || 0;
          const color = G.drawnGoods[placedCount];

          moves.executeProduction({
            placements: [{ side, column: columnNumber, columnType, row, color }],
          });
        }
      : undefined,
    onShowGoodsDisplay,
    onHideGoodsDisplay,
  };
}

// ============================================================================
// 7. 自動処理フェーズ Props 生成
// ============================================================================

const PHASE_INFO: Record<number, { title: string; description: string }> = {
  6: { title: 'Collect Income', description: 'Collecting income based on your income level' },
  7: { title: 'Pay Expenses', description: 'Paying expenses based on shares and locomotive level' },
  8: { title: 'Income Reduction', description: 'Income level is reduced according to the reduction table' },
  10: { title: 'Advance Turn', description: 'Advancing to the next turn and resetting flags' },
};

/**
 * Phase 6-8, 10: 自動処理フェーズのUIプロパティを生成
 */
export function createAutoPhaseProps(
  G: BarbadosGameState,
  phaseNumber: number
): AutoPhaseProps {
  const player = G.players['0'] as BarbadosPlayer;
  const info = PHASE_INFO[phaseNumber] || { title: `Phase ${phaseNumber}`, description: 'Processing...' };

  return {
    phaseTitle: info.title,
    phaseDescription: info.description,
    phaseNumber,
    player: {
      cash: player.cash,
      incomeLevel: player.incomeLevel,
      sharesIssued: player.sharesIssued,
      locomotiveLevel: player.locomotiveLevel,
    },
    diceAnimating: false, // アニメーションは親コンポーネントで管理
    diceValues: phaseNumber === 9 ? G.lastDiceRolls : undefined,
  };
}

// ============================================================================
// 8. 共通コンポーネント Props 生成
// ============================================================================


/**
 * 収入減少を計算（マップ設定に基づく）
 */
function calculateIncomeReduction(
  incomeLevel: number,
  incomeReductionFormula: 'standard' | 'detroit' = 'standard'
): number {
  if (incomeReductionFormula === 'detroit') {
    return calcIncomeReductionDetroit(incomeLevel);
  }
  return calcIncomeReduction(incomeLevel);
}

/**
 * アクション名を取得（表示用）
 */
function getActionDisplayName(action: string | null): string | null {
  if (!action) return null;
  // ActionType の大文字スネークケースを Title Case に変換
  // 例: "ENGINEER" -> "Engineer", "FIRST_MOVE" -> "First Move"
  return action
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * プレイヤーステータスプロパティを生成
 */
export function createPlayerStatsProps(
  G: BarbadosGameState,
  onShowGoodsDisplay?: () => void
): PlayerStatsProps {
  const player = G.players['0'] as BarbadosPlayer;
  const currentTurn = G.actualTurn || 1;
  const mapConfig = G.mapConfig;

  // 支出を計算（Phase 7 と同一ロジック）
  const expenses = computeTotalExpenses(G);

  // 収入減少を計算
  const incomeReduction = calculateIncomeReduction(
    player.incomeLevel,
    mapConfig?.incomeReductionFormula || 'standard'
  );

  // アクション名を取得
  const selectedActionName = getActionDisplayName(player.selectedAction);

  return {
    player: {
      cash: player.cash,
      incomeLevel: player.incomeLevel,
      sharesIssued: player.sharesIssued,
      locomotiveLevel: player.locomotiveLevel,
      isBankrupt: player.isBankrupt,
    },
    currentTurn,
    maxTurns: mapConfig?.maxTurns ?? 10,
    maxSharesLimit: mapConfig?.maxSharesLimit ?? 15,
    onShowGoodsDisplay,
    // 計算済み情報
    expenses,
    incomeReduction,
    selectedActionName,
    // Seattle 固有: 貨物輸送レベル（G に存在する場合のみ）
    ...('cargoTransportLevel' in G && {
      cargoTransportLevel: (G as { cargoTransportLevel: number }).cargoTransportLevel,
      maxCargoTransportLevel: 5,
    }),
  };
}

/**
 * ゲーム終了画面プロパティを生成
 */
export function createGameEndScreenProps(
  G: BarbadosGameState,
  onNewGame: () => void
): GameEndScreenProps {
  const player = G.players['0'] as BarbadosPlayer;

  return {
    endReason: G.endReason as 'VICTORY' | 'DEFEAT' | 'BANKRUPTCY',
    player: {
      cash: player.cash,
      sharesIssued: player.sharesIssued,
      incomeLevel: player.incomeLevel,
      locomotiveLevel: player.locomotiveLevel,
      finalCash: player.finalCash,
      isVictorious: player.isVictorious,
    },
    completedLinksCount: G.completedLinks.length,
    actualTurn: G.actualTurn || 10,
    onNewGame,
  };
}

// ============================================================================
// Seattle Metro 専用オーバーレイデータ生成
// ============================================================================

/**
 * Seattle Metro マップオーバーレイデータを生成
 * - ダイス番号バッジ（各ロケーション）
 * - 輸送済みキューブ数
 * - 需要過多警告（空きスロット2以下）
 */
export function createSeattleOverlayData(G: BarbadosGameState): SeattleMapOverlayData | null {
  // Seattle Metro 以外では null を返す
  if (G.mapConfig?.name !== 'Seattle Metro Solo') {
    return null;
  }

  const hexes = G.map;

  // 1. ダイス番号バッジを生成
  const diceBadges: SeattleMapOverlayData['diceBadges'] = [];
  for (const group of SEATTLE_DICE_GROUPS) {
    for (const locationId of group.locationIds) {
      // ヘクスを検索（City または Town）
      const hex = hexes.find(h =>
        h.city?.id === locationId || h.town?.id === locationId
      );
      if (hex) {
        diceBadges.push({
          locationId,
          diceValue: group.diceValue,
          coordinate: hex.coordinate,
        });
      }
    }
  }

  // 2. 輸送済みキューブ数を取得
  const player = G.players['0'];
  const deliveredCubes = ('deliveredCubes' in player) ? (player as { deliveredCubes: number }).deliveredCubes : 0;

  // 3. 需要過多警告を生成（空きスロット2以下のグループ）
  const warnings: SeattleMapOverlayData['warnings'] = [];
  const maxGoodsPerLocation = SEATTLE_RULES.maxGoodsPerLocation; // 2

  for (const group of SEATTLE_DICE_GROUPS) {
    let totalRemainingSlots = 0;
    let sumQ = 0;
    let sumR = 0;
    let count = 0;

    for (const locationId of group.locationIds) {
      const hex = hexes.find(h =>
        h.city?.id === locationId || h.town?.id === locationId
      );
      if (hex) {
        const currentGoods = hex.goods?.length ?? 0;
        const remaining = maxGoodsPerLocation - currentGoods;
        totalRemainingSlots += Math.max(0, remaining);
        sumQ += hex.coordinate.q;
        sumR += hex.coordinate.r;
        count++;
      }
    }

    // 空きスロットが2以下の場合に警告
    if (totalRemainingSlots <= 2 && count > 0) {
      warnings.push({
        diceValue: group.diceValue,
        remainingSlots: totalRemainingSlots,
        centerCoordinate: {
          q: Math.round(sumQ / count),
          r: Math.round(sumR / count),
        },
      });
    }
  }

  // 4. Airport 満杯警告（3個で満杯）
  const airportHex = hexes.find(h => h.city?.id === 'AIRPORT');
  if (airportHex) {
    const airportGoods = airportHex.goods?.length ?? 0;
    const airportCapacity = SEATTLE_RULES.airportGoodsCapacity; // 3
    if (airportGoods >= airportCapacity) {
      warnings.push({
        diceValue: 0, // 0 = Airport 用の特殊値
        remainingSlots: 0,
        centerCoordinate: airportHex.coordinate,
      });
    }
  }

  return {
    diceBadges,
    deliveredCubes,
    warnings,
  };
}
